/**
 * NotificationLeadingIcon
 *
 * Leading slot for notification rows that show a fixed icon instead of a user
 * avatar (schedule → `timer.png`, pay premium/boost → `premium_content.png`,
 * report → `report.png` over a circle). Ports the `SizedBox(50x50, Center(Image))`
 * leadings in `notification_schedule_content.dart:66-77`,
 * `notification_pay_premium_video_item.dart:62-72`, and
 * `notification_report_content_item.dart:67-88`.
 */
import React from 'react';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';

export interface NotificationLeadingIconProps {
  /** Asset path under /assets/images, e.g. `/assets/images/timer.png`. */
  iconPath: string;
  /** Icon tint. When omitted the asset renders with its source colors. */
  iconColor?: string;
  /** Render the icon over a filled circular surface (report style). */
  circleBackground?: boolean;
  /**
   * Render the icon at the full container size rather than the inset
   * `icon.large` size (identity-verification `verify.png` style — Flutter
   * renders it at the full `profileImageMessagesSize`).
   */
  fillContainer?: boolean;
}

export const NotificationLeadingIcon: React.FC<NotificationLeadingIconProps> = ({
  iconPath,
  iconColor,
  circleBackground = false,
  fillContainer = false,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const iconSize = fillContainer ? responsive.avatar.medium : responsive.icon.large;

  return (
    <div
      style={{
        width: responsive.avatar.medium,
        height: responsive.avatar.medium,
        borderRadius: circleBackground ? responsive.borderRadius.round : undefined,
        backgroundColor: circleBackground ? theme.surface : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <AssetIcon
        path={iconPath}
        width={iconSize}
        height={iconSize}
        color={iconColor}
      />
    </div>
  );
};
